import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Truck, Users, ClipboardList, Clock, ArrowRight, AlertCircle, AlertTriangle,
  Wrench, X, FileText, Calendar, FileDown, MessageCircle, TrendingUp,
  TrendingDown, User, BarChart3, MapPin, Cog, Receipt
} from 'lucide-react';
import OperatorRanking from '../components/OperatorRanking';
import { authHeaders } from '../utils/api';
import { fetchJsonResilient } from '../utils/resilientFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// === OPERATOR DASHBOARD ===
const fmtBRLdash = (v) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function OperatorDashboard({ operatorStats, financeiro, periodOp, setPeriodOp }) {
  if (!operatorStats) return null;

  const alert = operatorStats.maintenance_alert;
  const fin = financeiro;

  return (
    <div className="space-y-6" data-testid="operator-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-5 lg:p-6">
        <div className="flex items-center gap-4">
          <div className="bg-yellow-500/20 p-3 rounded-full">
            <User size={28} className="text-yellow-500" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-white">Ola, {operatorStats.operator_name}!</h1>
            <p className="text-gray-400 text-sm">Acompanhe sua producao e registre horimetros</p>
          </div>
        </div>
      </div>

      {/* Seletor de período livre (Melhoria Ago/2026) */}
      <div className="flex flex-wrap items-end gap-3 bg-[#111] border border-gray-800 rounded-xl p-3" data-testid="operator-periodo-bar">
        <div className="flex items-center gap-1.5 text-gray-400 text-xs"><Calendar size={14} /> Período:</div>
        <div>
          <label className="block text-[10px] text-gray-500 mb-0.5">Início</label>
          <input type="date" value={periodOp.inicio} data-testid="operator-periodo-inicio"
            onChange={(e) => setPeriodOp(p => ({ ...p, inicio: e.target.value }))}
            className="bg-[#0A0A0A] border border-gray-700 rounded px-2 py-1 text-white text-xs" />
        </div>
        <div>
          <label className="block text-[10px] text-gray-500 mb-0.5">Fim</label>
          <input type="date" value={periodOp.fim} data-testid="operator-periodo-fim"
            onChange={(e) => setPeriodOp(p => ({ ...p, fim: e.target.value }))}
            className="bg-[#0A0A0A] border border-gray-700 rounded px-2 py-1 text-white text-xs" />
        </div>
        <span className="text-[10px] text-gray-500 pb-1.5">Todos os cards refletem o período selecionado</span>
      </div>

      {/* Alerta de manutenção (apenas máquina mais usada) */}
      {alert && (
        <div className={`rounded-xl p-4 ${alert.status === 'urgent' ? 'bg-red-500/10 border border-red-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
          <div className="flex items-center gap-3">
            {alert.status === 'urgent' ? <AlertCircle className="text-red-400 shrink-0" size={20} /> : <AlertTriangle className="text-yellow-400 shrink-0" size={20} />}
            <div className="flex-1">
              <p className={`font-semibold text-sm ${alert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'}`}>
                {alert.machine_name} ({alert.serial_number})
              </p>
              <p className="text-gray-400 text-xs">
                Restam {alert.hours_until_maintenance}h para revisao
              </p>
            </div>
            <Link to="/machines" className="text-gray-400 hover:text-white text-xs">Ver</Link>
          </div>
        </div>
      )}

      {/* Cards de estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-yellow-500/50 transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-yellow-500/10 p-1.5 rounded-lg"><Clock size={16} className="text-yellow-500" /></div>
          </div>
          <p className="text-xl lg:text-2xl font-bold text-white">{operatorStats.hours_this_month}h</p>
          <p className="text-gray-400 text-xs">Horas no período</p>
          {operatorStats.variation_percent !== 0 && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs ${operatorStats.variation_percent > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {operatorStats.variation_percent > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              <span>{Math.abs(operatorStats.variation_percent)}%</span>
            </div>
          )}
        </div>

        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-blue-500/50 transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-blue-500/10 p-1.5 rounded-lg"><BarChart3 size={16} className="text-blue-400" /></div>
          </div>
          <p className="text-xl lg:text-2xl font-bold text-white">{operatorStats.total_hours}h</p>
          <p className="text-gray-400 text-xs">Total acumulado</p>
        </div>

        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-green-500/50 transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-green-500/10 p-1.5 rounded-lg"><ClipboardList size={16} className="text-green-400" /></div>
          </div>
          <p className="text-xl lg:text-2xl font-bold text-white">{operatorStats.total_orders}</p>
          <p className="text-gray-400 text-xs">Total de O.S.</p>
        </div>

        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-orange-500/50 transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-orange-500/10 p-1.5 rounded-lg"><AlertCircle size={16} className="text-orange-400" /></div>
          </div>
          <p className="text-xl lg:text-2xl font-bold text-white">{operatorStats.open_orders}</p>
          <p className="text-gray-400 text-xs">O.S. em aberto</p>
        </div>

        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-purple-500/50 transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-purple-500/10 p-1.5 rounded-lg"><Truck size={16} className="text-purple-400" /></div>
          </div>
          <p className="text-xl lg:text-2xl font-bold text-white">{operatorStats.machines_count || 0}</p>
          <p className="text-gray-400 text-xs">Máquinas/Veículos no mês</p>
        </div>
      </div>

      {/* Cards financeiros (Melhoria Ago/2026) — Conta Corrente do colaborador vinculado */}
      {fin && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="operator-financeiro-cards">
          <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-yellow-500/50 transition-all" data-testid="card-horas-produzidas">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-yellow-500/10 p-1.5 rounded-lg"><Clock size={16} className="text-yellow-500" /></div>
              <p className="text-gray-400 text-xs">Horas Produzidas</p>
            </div>
            <p className="text-xl lg:text-2xl font-bold text-white">{fin.horas_produzidas}h</p>
            <p className="text-gray-500 text-[11px] mt-1">
              {fin.valor_hora > 0
                ? `${fmtBRLdash(fin.valor_hora)}/h → ${fmtBRLdash(fin.valor_total_horas)}`
                : `${fin.os_fechadas} O.S. fechada(s)`}
            </p>
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-blue-500/50 transition-all" data-testid="card-diarias">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-blue-500/10 p-1.5 rounded-lg"><Receipt size={16} className="text-blue-400" /></div>
              <p className="text-gray-400 text-xs">Diárias</p>
            </div>
            <p className="text-xl lg:text-2xl font-bold text-blue-300">{fmtBRLdash(fin.diarias)}</p>
            <p className="text-gray-500 text-[11px] mt-1">creditadas no período</p>
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-red-500/50 transition-all" data-testid="card-retiradas">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-red-500/10 p-1.5 rounded-lg"><TrendingDown size={16} className="text-red-400" /></div>
              <p className="text-gray-400 text-xs">Retiradas</p>
            </div>
            <p className="text-xl lg:text-2xl font-bold text-red-400">{fmtBRLdash(fin.retiradas)}</p>
            <p className="text-gray-500 text-[11px] mt-1">adiantamentos e débitos</p>
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-xl p-4 hover:border-green-500/50 transition-all" data-testid="card-saldo-real">
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-1.5 rounded-lg ${fin.saldo_real >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                <TrendingUp size={16} className={fin.saldo_real >= 0 ? 'text-green-400' : 'text-red-400'} />
              </div>
              <p className="text-gray-400 text-xs">Saldo Real</p>
            </div>
            <p className={`text-xl lg:text-2xl font-bold ${fin.saldo_real >= 0 ? 'text-green-400' : 'text-red-400'}`}
               data-testid="saldo-real-valor">
              {fmtBRLdash(fin.saldo_real)}
            </p>
            <p className="text-gray-500 text-[11px] mt-1">créditos − débitos no período</p>
          </div>
        </div>
      )}

      {/* Progressão diária */}
      {operatorStats.daily_hours && operatorStats.daily_hours.length > 0 && (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={18} className="text-yellow-500" />
            <h2 className="text-base font-semibold text-white">Progressao do Mes</h2>
          </div>
          <div className="flex items-end gap-1 h-28 overflow-x-auto pb-2">
            {operatorStats.daily_hours.map((day, index) => {
              const maxHours = Math.max(...operatorStats.daily_hours.map(d => d.hours));
              const height = maxHours > 0 ? (day.hours / maxHours) * 100 : 0;
              const dayNum = day.date.split('-')[2];
              return (
                <div key={index} className="flex flex-col items-center min-w-[28px]">
                  <div 
                    className="w-5 bg-gradient-to-t from-yellow-600 to-yellow-400 rounded-t transition-all hover:from-yellow-500 hover:to-yellow-300"
                    style={{ height: `${Math.max(height, 4)}%` }}
                    title={`${day.date}: ${day.hours}h`}
                  />
                  <span className="text-gray-500 text-[10px] mt-1">{dayNum}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Últimas O.S. (enriquecidas) */}
      <div className="bg-[#111] border border-gray-800 rounded-xl">
        <div className="p-4 lg:p-5 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="text-yellow-500" size={18} />
              <h2 className="text-base font-semibold text-white">Minhas Ultimas O.S.</h2>
            </div>
            <Link to="/work-orders" data-testid="view-all-orders-link"
              className="text-yellow-500 hover:text-yellow-400 text-sm flex items-center gap-1">
              Ver todas <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        
        <div className="p-3 lg:p-4">
          {operatorStats.recent_orders && operatorStats.recent_orders.length > 0 ? (
            <div className="space-y-2">
              {operatorStats.recent_orders.map((order) => (
                <Link key={order.id} to="/work-orders" data-testid={`recent-order-${order.id}`}
                  className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-gray-800 hover:border-gray-700 transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`p-1.5 rounded-lg shrink-0 ${order.status === 'open' ? 'bg-orange-500/10' : 'bg-green-500/10'}`}>
                      <ClipboardList size={14} className={order.status === 'open' ? 'text-orange-400' : 'text-green-400'} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">
                        {order.order_number ? `#${order.order_number}` : ''} {order.machine}
                      </p>
                      <div className="flex items-center gap-2 text-gray-500 text-xs mt-0.5 flex-wrap">
                        <span>{order.date}</span>
                        {order.operation_type && <><span className="text-gray-700">|</span><span className="text-blue-400">{order.operation_type}</span></>}
                        {order.location && <><span className="text-gray-700">|</span><span className="flex items-center gap-0.5"><MapPin size={10} />{order.location}</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    {order.hours_worked !== null ? (
                      <p className="text-yellow-500 font-semibold text-sm">{order.hours_worked}h</p>
                    ) : order.start_hour_meter !== null ? (
                      <p className="text-orange-400 text-xs">Inicio: {order.start_hour_meter}h</p>
                    ) : (
                      <span className="text-orange-400 text-xs">Em aberto</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <ClipboardList size={40} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nenhuma O.S. registrada</p>
              <Link to="/work-orders" className="text-yellow-500 hover:text-yellow-400 text-sm mt-2 inline-block">
                Criar primeira O.S.
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Ações rápidas */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/work-orders" data-testid="quick-new-os"
          className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-xl p-4 text-center transition-colors">
          <ClipboardList size={24} className="mx-auto mb-1" />
          <span className="text-sm">Nova O.S.</span>
        </Link>
        <Link to="/work-orders" data-testid="quick-view-os"
          className="bg-[#111] border border-gray-800 hover:border-yellow-500/50 text-white rounded-xl p-4 text-center transition-colors">
          <FileText size={24} className="mx-auto mb-1 text-yellow-500" />
          <span className="text-sm">Minhas O.S.</span>
        </Link>
      </div>
    </div>
  );
}

// === MAIN DASHBOARD ===
export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [operatorStats, setOperatorStats] = useState(null);
  const [operatorFin, setOperatorFin] = useState(null);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [contasVencidas, setContasVencidas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAlertBanner, setShowAlertBanner] = useState(true);
  const [periodOp, setPeriodOp] = useState(() => {
    const now = new Date();
    const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    return { inicio: first, fim: now.toISOString().slice(0, 10) };
  });
  
  const isOperator = user?.role === 'operador';

  useEffect(() => {
    fetchData();
    /* eslint-disable-next-line */
  }, [periodOp]);

  const fetchData = async () => {
    try {
      const headers = authHeaders();
      const opts = { headers, cache: 'no-store' };

      if (isOperator) {
        const qs = `?data_inicio=${periodOp.inicio}&data_fim=${periodOp.fim}`;
        const [operatorData, finData] = await Promise.all([
          fetchJsonResilient(`${API_URL}/api/dashboard/operator-stats${qs}`, opts),
          fetchJsonResilient(`${API_URL}/api/dashboard/operator-financeiro${qs}`, opts),
        ]);
        if (operatorData) setOperatorStats(operatorData);
        if (finData) setOperatorFin(finData);
      } else {
        const [statsData, alertsData] = await Promise.all([
          fetchJsonResilient(`${API_URL}/api/dashboard/stats`, opts),
          fetchJsonResilient(`${API_URL}/api/maintenance/alerts`, opts)
        ]);
        if (statsData) setStats(statsData);
        if (alertsData) setMaintenanceAlerts(alertsData);

        // Contas Vencidas — só busca se usuário tem permissão; falha silenciosa em 403
        const podeVerContas = (
          user?.is_master || user?.is_master_auxiliar ||
          ['admin', 'gestor'].includes(user?.role) ||
          user?.acesso_contas_pagar || user?.acesso_financeiro || user?.acesso_almoxarifado
        );
        if (podeVerContas) {
          try {
            const [tot, lista] = await Promise.all([
              fetchJsonResilient(`${API_URL}/api/contas-pagar/totalizadores`, opts),
              fetchJsonResilient(`${API_URL}/api/contas-pagar?status_filtro=vencido`, opts),
            ]);
            if (tot && lista) {
              setContasVencidas({
                total: tot?.vencido || 0,
                quantidade: tot?.qtd_vencido || 0,
                itens: Array.isArray(lista) ? lista.slice(0, 3) : [],
              });
            }
          } catch (e) {
            // silencioso — widget é opcional
          }
        }
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro de conexao');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Dashboard do Operador
  if (isOperator) {
    return <OperatorDashboard operatorStats={operatorStats} financeiro={operatorFin} periodOp={periodOp} setPeriodOp={setPeriodOp} />;
  }

  // Dashboard padrão para gestores
  const urgentAlerts = maintenanceAlerts.filter(a => a.status === 'urgent');
  const attentionAlerts = maintenanceAlerts.filter(a => a.status === 'attention');

  const statCards = [
    { label: 'Máquinas e Veículos', value: stats?.total_machines || 0, icon: Truck, color: 'bg-blue-500', link: '/machines' },
    { label: 'Operadores', value: stats?.total_operators || 0, icon: Users, color: 'bg-green-500', link: '/operators' },
    { label: 'O.S. Abertas', value: stats?.open_orders || 0, icon: ClipboardList, color: 'bg-orange-500', link: '/work-orders' },
    { label: 'Horas Totais', value: stats?.total_hours?.toFixed(1) || '0', icon: Clock, color: 'bg-yellow-500', link: '/work-orders' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Widget de Contas Vencidas */}
      {contasVencidas && contasVencidas.quantidade > 0 && (
        <div
          className="rounded-xl p-4 lg:p-5 bg-red-500/10 border border-red-500/30"
          data-testid="widget-contas-vencidas"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="bg-red-500/20 p-2.5 rounded-lg shrink-0">
                <Receipt className="text-red-400" size={22} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-red-400" data-testid="widget-contas-vencidas-title">
                  {contasVencidas.quantidade} conta{contasVencidas.quantidade > 1 ? 's' : ''} vencida{contasVencidas.quantidade > 1 ? 's' : ''} • Total {contasVencidas.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </h3>
                <p className="text-gray-400 text-xs mt-0.5">
                  Obrigações em aberto com data de vencimento já ultrapassada.
                </p>
                {contasVencidas.itens.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {contasVencidas.itens.map((c) => (
                      <li key={c.id} className="flex items-center gap-2 text-sm">
                        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                        <span className="text-white truncate">{c.descricao}</span>
                        {c.fornecedor_nome && (
                          <span className="text-gray-500 truncate hidden sm:inline">• {c.fornecedor_nome}</span>
                        )}
                        <span className="text-red-400 ml-auto shrink-0">
                          {Number(c.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                        <span className="text-gray-500 text-xs shrink-0 hidden md:inline">venc. {c.data_vencimento}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <Link
                  to="/contas-a-pagar"
                  className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-red-400 hover:text-red-300"
                  data-testid="widget-contas-vencidas-link"
                >
                  Ver Contas a Pagar <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner de Alertas */}
      {showAlertBanner && maintenanceAlerts.length > 0 && (
        <div className={`rounded-xl p-4 ${urgentAlerts.length > 0 ? 'bg-red-500/10 border border-red-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {urgentAlerts.length > 0 ? <AlertCircle className="text-red-400 mt-0.5" size={24} /> : <AlertTriangle className="text-yellow-400 mt-0.5" size={24} />}
              <div>
                <h3 className={`font-semibold ${urgentAlerts.length > 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                  {urgentAlerts.length > 0 ? `${urgentAlerts.length} maquina(s) precisam de revisao URGENTE!` : `${attentionAlerts.length} maquina(s) com revisao proxima`}
                </h3>
                <div className="mt-2 space-y-1">
                  {maintenanceAlerts.slice(0, 3).map((alert) => (
                    <div key={alert.machine_id} className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full ${alert.status === 'urgent' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                      <span className="text-white">{alert.machine_name}</span>
                      <span className="text-gray-500">({alert.serial_number})</span>
                      <span className={alert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'}>- {alert.hours_until_maintenance}h restantes</span>
                    </div>
                  ))}
                  {maintenanceAlerts.length > 3 && <p className="text-gray-400 text-sm">+ {maintenanceAlerts.length - 3} outras maquinas</p>}
                </div>
                <Link to="/machines" className={`inline-flex items-center gap-1 mt-3 text-sm font-medium ${urgentAlerts.length > 0 ? 'text-red-400 hover:text-red-300' : 'text-yellow-400 hover:text-yellow-300'}`}>
                  Ver todas as maquinas <ArrowRight size={14} />
                </Link>
              </div>
            </div>
            <button onClick={() => setShowAlertBanner(false)} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">Painel de Controle</h1>
        <p className="text-gray-400 mt-1">Visao geral das operacoes</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.label} to={stat.link}
            className="bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6 hover:border-gray-700 transition-all group"
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-400 text-sm">{stat.label}</p>
                <p className="text-2xl lg:text-3xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-2 rounded-lg`}><stat.icon size={20} className="text-white" /></div>
            </div>
            <div className="flex items-center gap-1 mt-4 text-gray-500 group-hover:text-yellow-500 transition-colors text-sm">
              <span>Ver detalhes</span><ArrowRight size={14} />
            </div>
          </Link>
        ))}
      </div>

      {/* Card de Manutenção Preventiva */}
      {maintenanceAlerts.length > 0 && (
        <div className="bg-[#111] border border-gray-800 rounded-xl">
          <div className="p-4 lg:p-6 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wrench className="text-yellow-500" size={20} />
                <h2 className="text-lg font-semibold text-white">Revisoes Pendentes</h2>
              </div>
              <Link to="/machines" className="text-yellow-500 hover:text-yellow-400 text-sm flex items-center gap-1">Ver maquinas <ArrowRight size={14} /></Link>
            </div>
          </div>
          <div className="divide-y divide-gray-800">
            {maintenanceAlerts.map((alert) => (
              <div key={alert.machine_id} className="p-4 lg:p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${alert.status === 'urgent' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                  <div>
                    <p className="text-white font-medium">{alert.machine_name}</p>
                    <p className="text-gray-500 text-sm">{alert.serial_number}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${alert.status === 'urgent' ? 'text-red-400' : 'text-yellow-400'}`}>{alert.hours_until_maintenance}h</p>
                  <p className="text-gray-500 text-xs">restantes</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Link to="/machines" className="bg-[#111] border border-gray-800 rounded-xl p-6 hover:border-yellow-500/50 transition-all group">
          <div className="flex items-center gap-4">
            <div className="bg-yellow-500/10 p-3 rounded-lg group-hover:bg-yellow-500/20 transition-colors"><Truck size={24} className="text-yellow-500" /></div>
            <div><h3 className="text-white font-semibold">Cadastrar Máquina ou Veículo</h3><p className="text-gray-500 text-sm">Adicione máquinas e veículos da frota</p></div>
          </div>
        </Link>
        <Link to="/operators" className="bg-[#111] border border-gray-800 rounded-xl p-6 hover:border-yellow-500/50 transition-all group">
          <div className="flex items-center gap-4">
            <div className="bg-yellow-500/10 p-3 rounded-lg group-hover:bg-yellow-500/20 transition-colors"><Users size={24} className="text-yellow-500" /></div>
            <div><h3 className="text-white font-semibold">Cadastrar Operador</h3><p className="text-gray-500 text-sm">Registre novos operadores</p></div>
          </div>
        </Link>
        <Link to="/work-orders" className="bg-[#111] border border-gray-800 rounded-xl p-6 hover:border-yellow-500/50 transition-all group">
          <div className="flex items-center gap-4">
            <div className="bg-yellow-500/10 p-3 rounded-lg group-hover:bg-yellow-500/20 transition-colors"><ClipboardList size={24} className="text-yellow-500" /></div>
            <div><h3 className="text-white font-semibold">Nova O.S.</h3><p className="text-gray-500 text-sm">Registre horas trabalhadas</p></div>
          </div>
        </Link>
      </div>

      {/* Recent Work Orders */}
      <div className="bg-[#111] border border-gray-800 rounded-xl">
        <div className="p-4 lg:p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Ultimas Ordens de Servico</h2>
            <Link to="/work-orders" className="text-yellow-500 hover:text-yellow-400 text-sm flex items-center gap-1">Ver todas <ArrowRight size={14} /></Link>
          </div>
        </div>
        {stats?.recent_orders?.length > 0 ? (
          <div className="divide-y divide-gray-800">
            {stats.recent_orders.map((order) => (
              <div key={order.id} className="p-4 lg:p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-2 rounded-full ${order.status === 'open' ? 'bg-orange-500' : 'bg-green-500'}`} />
                  <div>
                    <p className="text-white font-medium">{order.machine}</p>
                    <p className="text-gray-500 text-sm">{order.operator} - {order.date}</p>
                  </div>
                </div>
                <div className="text-right">
                  {order.hours_worked !== null ? (
                    <p className="text-white font-semibold">{order.hours_worked}h</p>
                  ) : (
                    <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">Em andamento</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <AlertCircle size={40} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">Nenhuma ordem de servico registrada</p>
            <Link to="/work-orders" className="inline-flex items-center gap-2 mt-4 text-yellow-500 hover:text-yellow-400">Criar primeira O.S. <ArrowRight size={16} /></Link>
          </div>
        )}
      </div>

      {/* Relatórios Rápidos */}
      <div className="bg-[#111] border border-gray-800 rounded-xl">
        <div className="p-4 lg:p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><FileText className="text-yellow-500" size={20} /><h2 className="text-lg font-semibold text-white">Relatorios Rapidos</h2></div>
            <Link to="/reports" className="text-yellow-500 hover:text-yellow-400 text-sm flex items-center gap-1">Ver todos <ArrowRight size={14} /></Link>
          </div>
        </div>
        <div className="p-4 lg:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <a href={`${API_URL}/api/reports/export-pdf?date_from=${new Date().toISOString().split('T')[0]}&date_to=${new Date().toISOString().split('T')[0]}`} target="_blank" rel="noopener noreferrer"
              className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-yellow-500/50 transition-all text-center group">
              <div className="bg-blue-500/10 p-3 rounded-lg mx-auto w-fit group-hover:bg-blue-500/20 transition-colors mb-2"><Calendar size={20} className="text-blue-400" /></div>
              <p className="text-white text-sm font-medium">Diario</p><p className="text-gray-500 text-xs">Hoje</p>
            </a>
            <a href={`${API_URL}/api/reports/export-pdf?date_from=${new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}&date_to=${new Date().toISOString().split('T')[0]}`} target="_blank" rel="noopener noreferrer"
              className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-yellow-500/50 transition-all text-center group">
              <div className="bg-purple-500/10 p-3 rounded-lg mx-auto w-fit group-hover:bg-purple-500/20 transition-colors mb-2"><FileDown size={20} className="text-purple-400" /></div>
              <p className="text-white text-sm font-medium">Semanal</p><p className="text-gray-500 text-xs">Ultimos 7 dias</p>
            </a>
            <button onClick={() => { const today = new Date().toISOString().split('T')[0]; const [y, m, d] = today.split('-'); const pdfUrl = encodeURIComponent(`${API_URL}/api/reports/export-pdf?date_from=${today}&date_to=${today}`); window.open(`https://wa.me/?text=${encodeURIComponent(`Relatorio Diario - ${d}/${m}/${y}\n${decodeURIComponent(pdfUrl)}`)}`, '_blank'); }}
              className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-green-500/50 transition-all text-center group">
              <div className="bg-green-500/10 p-3 rounded-lg mx-auto w-fit group-hover:bg-green-500/20 transition-colors mb-2"><MessageCircle size={20} className="text-green-400" /></div>
              <p className="text-white text-sm font-medium">WhatsApp</p><p className="text-gray-500 text-xs">Diario</p>
            </button>
            <button onClick={() => { const today = new Date().toISOString().split('T')[0]; const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; window.open(`https://wa.me/?text=${encodeURIComponent(`Relatorio Semanal\n${API_URL}/api/reports/export-pdf?date_from=${weekAgo}&date_to=${today}`)}`, '_blank'); }}
              className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 hover:border-green-500/50 transition-all text-center group">
              <div className="bg-emerald-500/10 p-3 rounded-lg mx-auto w-fit group-hover:bg-emerald-500/20 transition-colors mb-2"><MessageCircle size={20} className="text-emerald-400" /></div>
              <p className="text-white text-sm font-medium">WhatsApp</p><p className="text-gray-500 text-xs">Semanal</p>
            </button>
          </div>
        </div>
      </div>

      {/* Ranking de Operadores */}
      <OperatorRanking />
    </div>
  );
}
